import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useMember, useMembersList } from '@/hooks/use-members'
import { usePlot, usePlotsList } from '@/hooks/use-plots'
import { useUpdateDocument, useUploadDocument } from '@/hooks/use-documents'
import { getApiErrorMessage } from '@/lib/api'
import type { DocumentCategory, DocumentVisibility, EstateDocument } from '@riverpark/types'

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

const CATEGORIES: { value: DocumentCategory; label: string }[] = [
  { value: 'agm_minutes', label: 'AGM minutes' },
  { value: 'financial_report', label: 'Financial report' },
  { value: 'notice', label: 'Notice' },
  { value: 'title_deed', label: 'Title deed' },
  { value: 'other', label: 'Other' },
]

const VISIBILITIES: { value: DocumentVisibility; label: string }[] = [
  { value: 'all_members', label: 'All members' },
  { value: 'specific_member', label: 'A specific member' },
  { value: 'specific_plot', label: "A specific plot's owner" },
  { value: 'public', label: 'Everyone, incl. the public website' },
]

export function UploadDocumentModal({
  document,
  onClose,
}: {
  /** Omit to upload a new document instead of editing one. */
  document?: EstateDocument
  onClose: () => void
}) {
  const isEdit = !!document
  const upload = useUploadDocument()
  const update = useUpdateDocument()
  const pending = upload.isPending || update.isPending

  const [title, setTitle] = useState(document?.title ?? '')
  const [category, setCategory] = useState<DocumentCategory>(document?.category ?? 'notice')
  const [visibility, setVisibility] = useState<DocumentVisibility>(
    document?.visibility ?? 'all_members',
  )
  const [memberId, setMemberId] = useState(document?.memberId ?? '')
  const [plotId, setPlotId] = useState(document?.plotId ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const { data: memberData } = useMembersList({
    page: 1,
    limit: 20,
    search: debouncedSearch || undefined,
  })
  const members = visibility === 'specific_member' ? (memberData?.data ?? []) : []
  const { data: currentMemberData } = useMember(
    visibility === 'specific_member' ? memberId || undefined : undefined,
  )
  const currentMember = currentMemberData?.data

  const { data: plotData } = usePlotsList({
    page: 1,
    limit: 20,
    search: debouncedSearch || undefined,
  })
  const plots = visibility === 'specific_plot' ? (plotData?.data ?? []) : []
  const { data: currentPlotData } = usePlot(
    visibility === 'specific_plot' ? plotId || undefined : undefined,
  )
  const currentPlot = currentPlotData?.data

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!isEdit && !file) {
      setError('Choose a file to upload')
      return
    }
    try {
      const input = {
        title,
        category,
        visibility,
        ...(visibility === 'specific_member' && { memberId }),
        ...(visibility === 'specific_plot' && { plotId }),
      }
      if (isEdit) {
        await update.mutateAsync({
          id: document.id,
          input: { ...input, ...(file && { file }) },
        })
      } else {
        await upload.mutateAsync({ ...input, file: file! })
      }
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, `Failed to ${isEdit ? 'update' : 'upload'} document`))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="upload-document-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <h2 id="upload-document-title" className="font-serif text-xl text-ink">
            {isEdit ? 'Edit document' : 'Upload document'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            minLength={3}
            maxLength={200}
          />

          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink">Category</label>
            <select
              className={SELECT_CLS}
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentCategory)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink">Visible to</label>
            <select
              className={SELECT_CLS}
              value={visibility}
              onChange={(e) => {
                setVisibility(e.target.value as DocumentVisibility)
                setMemberId('')
                setPlotId('')
                setSearch('')
              }}
            >
              {VISIBILITIES.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>

          {visibility === 'specific_member' && (
            <div className="space-y-2">
              {currentMember && (
                <p className="font-sans text-xs text-muted-foreground">
                  Currently: <span className="font-medium text-ink">{currentMember.name}</span> —
                  search below to change.
                </p>
              )}
              <Input
                label="Search members"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, phone, email…"
              />
              <div className="max-h-48 overflow-y-auto rounded border border-border-2">
                {members.length === 0 ? (
                  <p className="px-3 py-4 font-sans text-sm text-muted-foreground">
                    No members found
                  </p>
                ) : (
                  members.map((m) => (
                    <label
                      key={m.id}
                      className="flex cursor-pointer items-start gap-3 border-b border-border-2 px-3 py-2 last:border-0 hover:bg-cream-2/50"
                    >
                      <input
                        type="radio"
                        name="member"
                        checked={memberId === m.id}
                        onChange={() => setMemberId(m.id)}
                        className="mt-1"
                      />
                      <span className="min-w-0 font-sans text-sm">
                        <span className="font-medium text-ink">{m.name}</span>
                        <span className="block text-xs text-muted-foreground">{m.phone}</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {visibility === 'specific_plot' && (
            <div className="space-y-2">
              {currentPlot && (
                <p className="font-sans text-xs text-muted-foreground">
                  Currently:{' '}
                  <span className="font-medium text-ink">Plot {currentPlot.newNumber}</span> —
                  search below to change.
                </p>
              )}
              <Input
                label="Search plots"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Plot number…"
              />
              <div className="max-h-48 overflow-y-auto rounded border border-border-2">
                {plots.length === 0 ? (
                  <p className="px-3 py-4 font-sans text-sm text-muted-foreground">
                    No plots found
                  </p>
                ) : (
                  plots.map((p) => (
                    <label
                      key={p.id}
                      className="flex cursor-pointer items-start gap-3 border-b border-border-2 px-3 py-2 last:border-0 hover:bg-cream-2/50"
                    >
                      <input
                        type="radio"
                        name="plot"
                        checked={plotId === p.id}
                        onChange={() => setPlotId(p.id)}
                        className="mt-1"
                      />
                      <span className="min-w-0 font-sans text-sm">
                        <span className="font-medium text-ink">Plot {p.newNumber}</span>
                        <span className="block text-xs text-muted-foreground">{p.status}</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink">File</label>
            <input
              type="file"
              required={!isEdit}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full font-sans text-sm text-ink"
            />
            <p className="font-sans text-xs text-muted-foreground">
              {isEdit
                ? 'Leave blank to keep the current file. PDF, Word, Excel, or image — up to 25MB.'
                : 'PDF, Word, Excel, or image — up to 25MB.'}
            </p>
          </div>

          {error && (
            <p className="font-sans text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={pending}
            disabled={
              (visibility === 'specific_member' && !memberId) ||
              (visibility === 'specific_plot' && !plotId)
            }
          >
            {isEdit ? 'Save changes' : 'Upload'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
